import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    ActivatedRoute,
    Event,
    NavigationStart,
    Router,
} from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class BreadscrumbService {
    activeTitle: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    breadsCrumb$: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>(
        [],
    );
    currentUrl: string = '';
    constructor(
        private router: Router,
        private _activatedRoute: ActivatedRoute,
    ) {
        /**
         * This run on every route change, for getting latest and current URL
         */
        this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationStart) {
                if (event?.url) {
                    this.currentUrl = event?.url;
                    this.getLatestBreadscrumbData();
                }
            }
        });
    }

    setBreadsCrumb(breadsCrumb): void {
        this.breadsCrumb$.next(breadsCrumb);
        sessionStorage.setItem('breadsCrumb', JSON.stringify(breadsCrumb));
    }

    getBreadsCrumb(): any {
        return JSON.parse(sessionStorage.getItem('breadsCrumb'));
    }

    getLatestBreadscrumbData(): void {
        const data = this.getBreadsCrumb();

        if (data?.length && this.currentUrl) {
            const splitLastBreadscrumb = data.at(-1)?.url?.split('?')[0];
            const splitCurrentUrl = this.currentUrl?.split('?')[0];
            if (splitCurrentUrl?.length - splitLastBreadscrumb?.length <= 2) {
                /**
                 * If you change the pagination or tab not the route,
                 * then you come inside this condition here we update the last breadscrumb url with the updated url.
                 */
                data.at(-1).url = this.currentUrl;
                this.setBreadsCrumb(data);
            }
        }
    }

    handleBreadsCrumb({
        name,
        url,
        reset = false,
        hideHeader = false,
        tooltip = '',
    }: {
        name: string;
        url?: string;
        reset?: boolean;
        hideHeader?: boolean;
        tooltip?: string;
    }): void {
        if (!this.currentUrl) {
            /**
             * If you reload the page, then currentUrl becomes empty,
             * but currentUrl needs must be always equal with the last breadscrumb url in storage.
             * so we take the last breadscrumbUrl from storage and assign it to the currentUrl,
             * so that breadscrumb link clicks works fine.
             */
            const data = this.getBreadsCrumb();
            if (data?.length) {
                this.currentUrl = data.at(-1)?.url;
            }
        }
        if (reset) {
            const arr: any = [];
            arr.push({
                name,
                url: this.currentUrl,
                hideHeader,
            });
            this.setBreadsCrumb(arr);
        } else {
            const breadsCrumb = this.getBreadsCrumb();

            if (breadsCrumb) {
                const index = breadsCrumb.findIndex(
                    (item: any) => item.tooltip === name,
                );

                if (index >= 0) {
                    breadsCrumb.splice(index + 1);
                } else {
                    const nameFromQueryParams =
                        this._activatedRoute.snapshot.queryParamMap.get(
                            'name',
                        ) ?? '';

                    breadsCrumb.push({
                        name: decodeURIComponent(nameFromQueryParams) || name,
                        url: this.currentUrl,
                        hideHeader,
                        tooltip: name || '',
                    });
                }
                this.setBreadsCrumb(breadsCrumb);
                this.handleBrowserBack(this.currentUrl, breadsCrumb);
            } else {
                const arr: any = [];
                arr.push({
                    name,
                    url: this.router?.url,
                    hideHeader,
                });
                this.setBreadsCrumb(arr);
            }
        }
    }

    handleBrowserBack(url: string, breadsCrumb: Array<any>): void {
        /**
         * This function handle the breadscrumb according to the browser back button.
         * In this case we replaced the secondlast breadscrumb url with the last Breadscrumb Url
         * and remove the last breadscrumb from storage
         */
        const urlLength = this.findUrlLength(url);
        if (breadsCrumb.length > 1) {
            const breadsCrumbLength = this.findUrlLength(
                breadsCrumb[breadsCrumb.length - 2].url,
            );
            if (urlLength < breadsCrumbLength) {
                breadsCrumb[breadsCrumb.length - 2] =
                    breadsCrumb[breadsCrumb.length - 1];
                breadsCrumb.splice(breadsCrumb.length - 1, 1);
                this.setBreadsCrumb(breadsCrumb);
            }
        }
    }

    findUrlLength(url: string): number {
        const indexOfQuestionMark = url.indexOf('?');
        const stringBeforeQuestionMark =
            indexOfQuestionMark !== -1
                ? url.substring(0, indexOfQuestionMark)
                : url;
        return stringBeforeQuestionMark.length;
    }

    getBreadsCrumb$(): Observable<Array<any>> {
        return this.breadsCrumb$.asObservable();
    }
}
